import * as THREE from 'three';
import { MIN_ZOOM, MAX_ZOOM } from '../constants';
import type { FloorPlanBounds } from '../types';
import type { ViewportState as CanvasViewportState } from '../store/viewportStore';

/** Height of orthographic camera above the floor plan (canvas Y → Three.js Z). */
export const ORTHO_CAMERA_HEIGHT = 1000;

/**
 * Default schematic 3D camera — front-elevated view matched to Blender
 * `amhs_fab.blend` Camera:
 *   Location (0, -22, 28) Z-up → Three.js Y-up offset (0, 28, 22) from target.
 * Vertical FOV ≈ 2·atan(12/28) for a 28 mm lens on a 24 mm sensor height.
 */
export const SCHEMATIC_3D_FOV_DEG = 46.4;
/** Orbit look-at height (floor plane) — keeps the full fab centered in frame. */
export const SCHEMATIC_3D_TARGET_Y = 0;
/** Unit direction from target → camera (Three.js Y-up). */
export const SCHEMATIC_3D_CAMERA_DIR = (() => {
  // Front-elevated (looks mostly along −Z).
  const x = 0;
  const y = 28;
  const z = 22;
  const len = Math.hypot(x, y, z) || 1;
  return { x: x / len, y: y / len, z: z / len } as const;
})();

function schematic3DCameraPosition(
  cx: number,
  cz: number,
  dist: number,
  targetY = SCHEMATIC_3D_TARGET_Y
): [number, number, number] {
  const d = SCHEMATIC_3D_CAMERA_DIR;
  return [cx + dist * d.x, targetY + dist * d.y, cz + dist * d.z];
}

const _fitCam = new THREE.PerspectiveCamera();
const _fitPt = new THREE.Vector3();

/** Max |NDC x/y| of ground-plane corners for a camera at `dist` from target. */
function schematic3DCornerNdcExtent(
  dist: number,
  cx: number,
  cz: number,
  targetY: number,
  corners: ReadonlyArray<readonly [number, number, number]>,
  aspect: number,
  fovDeg: number
): number {
  const [px, py, pz] = schematic3DCameraPosition(cx, cz, dist, targetY);
  _fitCam.fov = fovDeg;
  _fitCam.aspect = Math.max(aspect, 1e-6);
  _fitCam.near = Math.max(0.05, dist * 0.0005);
  _fitCam.far = Math.max(dist * 50, 1000);
  _fitCam.position.set(px, py, pz);
  _fitCam.up.set(0, 1, 0);
  _fitCam.lookAt(cx, targetY, cz);
  _fitCam.updateProjectionMatrix();
  _fitCam.updateMatrixWorld(true);

  let maxExtent = 0;
  for (const [x, y, z] of corners) {
    _fitPt.set(x, y, z).project(_fitCam);
    // Behind/near clip → treat as overflowing so search backs the camera out.
    if (!Number.isFinite(_fitPt.x) || !Number.isFinite(_fitPt.y) || _fitPt.z > 1) {
      return Number.POSITIVE_INFINITY;
    }
    maxExtent = Math.max(maxExtent, Math.abs(_fitPt.x), Math.abs(_fitPt.y));
  }
  return maxExtent;
}

export type FlatPlanCameraState = {
  centerX: number;
  centerZ: number;
  frustumWidth: number;
  frustumHeight: number;
  zoom: number;
};

/** Pixel insets for UI chrome overlaid on the full-bleed canvas. */
export type FitViewportInsets = {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
};

/**
 * Left toolbox / right drawer sit on top of the canvas (absolute), so fit must
 * center in the remaining visible region — not the full stage width.
 */
export function canvasChromeInsets(options?: {
  leftSidebarOpen?: boolean;
  rightSidebarOpen?: boolean;
}): FitViewportInsets {
  // Match Toolbox / RightPanel placement: left-3/right-3 + panel widths.
  const left = options?.leftSidebarOpen === false ? 16 : 136;
  const right = options?.rightSidebarOpen === false ? 68 : 332;
  return { left, right, top: 12, bottom: 72 };
}

/** Fit zoom/pan so a layout bounding box fills the canvas (matches 2D floor-plan fit). */
export function computeFitViewport(
  bounds: FloorPlanBounds,
  canvasWidth: number,
  canvasHeight: number,
  padding = 0.9,
  insets?: FitViewportInsets
): Pick<CanvasViewportState, 'zoom' | 'panX' | 'panY'> {
  const bw = Math.max(bounds.maxX - bounds.minX, 1);
  const bh = Math.max(bounds.maxY - bounds.minY, 1);
  const cw = Math.max(canvasWidth, 1);
  const ch = Math.max(canvasHeight, 1);
  const insetL = Math.max(0, insets?.left ?? 0);
  const insetR = Math.max(0, insets?.right ?? 0);
  const insetT = Math.max(0, insets?.top ?? 0);
  const insetB = Math.max(0, insets?.bottom ?? 0);
  const viewW = Math.max(cw - insetL - insetR, 1);
  const viewH = Math.max(ch - insetT - insetB, 1);

  const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(viewW / bw, viewH / bh) * padding));
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  const viewCenterX = insetL + viewW / 2;
  const viewCenterY = insetT + viewH / 2;

  return {
    zoom,
    panX: viewCenterX - centerX * zoom,
    panY: viewCenterY - centerY * zoom,
  };
}

/**
 * Derive an orthographic camera from the 2D Konva viewport so 3D flat plan
 * matches element size and framing on screen.
 */
export function flatPlanCameraFromViewport(
  viewport: CanvasViewportState,
  layoutBounds?: FloorPlanBounds | null
): FlatPlanCameraState {
  const canvasWidth = viewport.canvasWidth;
  const canvasHeight = viewport.canvasHeight;
  const zoom = viewport.zoom;

  if (canvasWidth > 0 && canvasHeight > 0 && zoom > 0) {
    return {
      centerX: (canvasWidth / 2 - viewport.panX) / zoom,
      centerZ: (canvasHeight / 2 - viewport.panY) / zoom,
      frustumWidth: canvasWidth,
      frustumHeight: canvasHeight,
      zoom,
    };
  }

  if (layoutBounds) {
    const bw = Math.max(layoutBounds.maxX - layoutBounds.minX, 500);
    const bh = Math.max(layoutBounds.maxY - layoutBounds.minY, 500);
    const fit = computeFitViewport(layoutBounds, 1200, 800);
    const centerX = (layoutBounds.minX + layoutBounds.maxX) / 2;
    const centerY = (layoutBounds.minY + layoutBounds.maxY) / 2;
    return {
      centerX,
      centerZ: centerY,
      frustumWidth: bw / fit.zoom,
      frustumHeight: bh / fit.zoom,
      zoom: fit.zoom,
    };
  }

  return {
    centerX: 0,
    centerZ: 0,
    frustumWidth: 2000,
    frustumHeight: 2000,
    zoom: 1,
  };
}

/** Perspective orbit camera fit for schematic 3D layouts (1:1 canvas coordinates). */
export function computeSchematic3DCameraFit(
  bounds: FloorPlanBounds,
  canvasWidth = 1200,
  canvasHeight = 800,
  fovDeg = SCHEMATIC_3D_FOV_DEG,
  /** Fraction of the view the fab AABB should fill (0.9 ≈ slight margin). */
  fill = 0.9
): {
  cx: number;
  cz: number;
  span: number;
  targetY: number;
  position: [number, number, number];
} {
  const bw = Math.max(bounds.maxX - bounds.minX, 1);
  const bh = Math.max(bounds.maxY - bounds.minY, 1);
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cz = (bounds.minY + bounds.maxY) / 2;
  const span = Math.max(bw, bh, 500);
  const aspect = Math.max(canvasWidth, 1) / Math.max(canvasHeight, 1);
  const targetY = SCHEMATIC_3D_TARGET_Y;
  const corners: Array<readonly [number, number, number]> = [
    [bounds.minX, targetY, bounds.minY],
    [bounds.maxX, targetY, bounds.minY],
    [bounds.minX, targetY, bounds.maxY],
    [bounds.maxX, targetY, bounds.maxY],
  ];

  // Project AABB corners and binary-search distance so the fab fills `fill` of NDC.
  // Analytical FOV formulas are wrong for elevated ground-plane views.
  const targetNdc = Math.min(Math.max(fill, 0.5), 0.98);
  let lo = Math.max(span * 0.02, 1);
  let hi = Math.max(span * 25, lo * 4);
  // Ensure hi is far enough that corners fit.
  for (let expand = 0; expand < 8; expand++) {
    const ext = schematic3DCornerNdcExtent(hi, cx, cz, targetY, corners, aspect, fovDeg);
    if (ext <= targetNdc) break;
    hi *= 2;
  }
  for (let i = 0; i < 28; i++) {
    const mid = (lo + hi) / 2;
    const ext = schematic3DCornerNdcExtent(mid, cx, cz, targetY, corners, aspect, fovDeg);
    if (ext > targetNdc) lo = mid;
    else hi = mid;
  }
  const dist = hi;

  return {
    cx,
    cz,
    span,
    targetY,
    position: schematic3DCameraPosition(cx, cz, dist, targetY),
  };
}

/**
 * Fit the 3D camera to show an entire layout / DXF bounding box.
 * Prefer this over viewport-sync when opening 3D so the full plan is visible.
 */
export function computeSchematic3DCameraFitBounds(
  bounds: FloorPlanBounds,
  canvasWidth: number,
  canvasHeight: number,
  fovDeg = SCHEMATIC_3D_FOV_DEG,
  fill = 0.9
) {
  return computeSchematic3DCameraFit(bounds, canvasWidth, canvasHeight, fovDeg, fill);
}

/**
 * Match the 2D Konva viewport framing (visible region only).
 * Use when live-syncing pan/zoom while 3D is open.
 */
export function computeSchematic3DCameraFromViewport(
  viewport: CanvasViewportState,
  layoutBounds: FloorPlanBounds | null,
  fovDeg = SCHEMATIC_3D_FOV_DEG
): {
  cx: number;
  cz: number;
  span: number;
  targetY: number;
  position: [number, number, number];
} {
  const cw = Math.max(viewport.canvasWidth, 1);
  const ch = Math.max(viewport.canvasHeight, 1);
  const zoom = Math.max(viewport.zoom, MIN_ZOOM);

  const targetY = SCHEMATIC_3D_TARGET_Y;

  if (cw > 0 && ch > 0) {
    const cx = (cw / 2 - viewport.panX) / zoom;
    const cz = (ch / 2 - viewport.panY) / zoom;
    const visibleW = cw / zoom;
    const visibleH = ch / zoom;
    // Rebuild from the visible ground rect with the same projector-fit as Fit.
    return computeSchematic3DCameraFit(
      {
        minX: cx - visibleW / 2,
        maxX: cx + visibleW / 2,
        minY: cz - visibleH / 2,
        maxY: cz + visibleH / 2,
      },
      cw,
      ch,
      fovDeg,
      1
    );
  }

  if (layoutBounds) {
    return computeSchematic3DCameraFit(layoutBounds, cw, ch, fovDeg);
  }

  const dist = 2000 * 1.35;
  return {
    cx: 0,
    cz: 0,
    span: 2000,
    targetY,
    position: schematic3DCameraPosition(0, 0, dist, targetY),
  };
}

export type OrthoWorldView = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

/** Visible canvas/world region from an orthographic top-down camera (live pan/zoom in 3D). */
export function getOrthoWorldView(
  camera: THREE.OrthographicCamera,
  centerX: number,
  centerZ: number
): OrthoWorldView {
  const zoom = Math.max(camera.zoom, 1e-6);
  const halfW = (camera.right - camera.left) / (2 * zoom);
  const halfH = (camera.top - camera.bottom) / (2 * zoom);
  return {
    minX: centerX - halfW,
    maxX: centerX + halfW,
    minY: centerZ - halfH,
    maxY: centerZ + halfH,
  };
}

/** Konva stage scale equivalent — pixels per canvas/world unit. */
export function getOrthoPixelsPerWorldUnit(
  camera: THREE.OrthographicCamera,
  viewportHeight: number
): number {
  const worldH = Math.max((camera.top - camera.bottom) / Math.max(camera.zoom, 1e-6), 1e-6);
  return viewportHeight / worldH;
}

const _ndc = new THREE.Vector3();
const _dir = new THREE.Vector3();

/** Ground-plane visible bounds for a perspective camera. */
export function getPerspectiveGroundView(
  camera: THREE.Camera,
  _viewportWidth: number,
  _viewportHeight: number,
  groundY = 0
): OrthoWorldView {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const nx of [-1, 1]) {
    for (const ny of [-1, 1]) {
      _ndc.set(nx, ny, 0.5);
      _ndc.unproject(camera);
      _dir.copy(_ndc).sub(camera.position);
      if (Math.abs(_dir.y) < 1e-8) continue;
      const t = (groundY - camera.position.y) / _dir.y;
      if (t < 0) continue;
      const x = camera.position.x + _dir.x * t;
      const z = camera.position.z + _dir.z * t;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, z);
      maxY = Math.max(maxY, z);
    }
  }

  if (!Number.isFinite(minX)) {
    return { minX: -5000, maxX: 5000, minY: -5000, maxY: 5000 };
  }

  const padX = (maxX - minX) * 0.08;
  const padY = (maxY - minY) * 0.08;
  return { minX: minX - padX, maxX: maxX + padX, minY: minY - padY, maxY: maxY + padY };
}

/** Screen pixels per world unit at a ground point (perspective camera). */
export function getPerspectivePixelsPerWorldUnit(
  camera: THREE.PerspectiveCamera,
  viewportHeight: number,
  groundX: number,
  groundZ: number,
  groundY = 0
): number {
  // Sample along the ground axis more perpendicular to the view so head-on
  // axes (front → Z, side → X) do not collapse the pixel delta toward 0.
  const p0 = new THREE.Vector3(groundX, groundY, groundZ);
  const pX = new THREE.Vector3(groundX + 1, groundY, groundZ);
  const pZ = new THREE.Vector3(groundX, groundY, groundZ + 1);
  p0.project(camera);
  pX.project(camera);
  pZ.project(camera);
  const viewportWidth = Math.max(viewportHeight * Math.max(camera.aspect, 1e-6), 1);
  const sx0 = (p0.x * 0.5 + 0.5) * viewportWidth;
  const sxX = (pX.x * 0.5 + 0.5) * viewportWidth;
  const sxZ = (pZ.x * 0.5 + 0.5) * viewportWidth;
  return Math.max(Math.abs(sxX - sx0), Math.abs(sxZ - sx0), 0);
}

/**
 * Configure a top-down orthographic camera to match the 2D Konva viewport exactly.
 * After this, one world unit spans the same number of screen pixels as in 2D.
 */
export function applyOrthoFromViewport(
  camera: THREE.OrthographicCamera,
  viewport: CanvasViewportState,
  canvasWidth: number,
  canvasHeight: number
): { cx: number; cz: number; span: number } {
  const cw = Math.max(canvasWidth, 1);
  const ch = Math.max(canvasHeight, 1);
  const zoom = Math.max(viewport.zoom, MIN_ZOOM);

  const halfW = cw / zoom / 2;
  const halfH = ch / zoom / 2;
  const cx = (cw / 2 - viewport.panX) / zoom;
  const cz = (ch / 2 - viewport.panY) / zoom;

  camera.left = -halfW;
  camera.right = halfW;
  camera.top = halfH;
  camera.bottom = -halfH;
  camera.zoom = 1;
  camera.position.set(cx, ORTHO_CAMERA_HEIGHT, cz);
  camera.up.set(0, 1, 0);
  camera.lookAt(cx, 0, cz);
  camera.near = 0.1;
  camera.far = ORTHO_CAMERA_HEIGHT * 4;
  camera.updateProjectionMatrix();

  return { cx, cz, span: Math.max(halfW, halfH) * 2 };
}

/** Fit full layout bounds in orthographic view (⊡ Fit button). */
export function applyOrthoFitBounds(
  camera: THREE.OrthographicCamera,
  bounds: FloorPlanBounds,
  canvasWidth: number,
  canvasHeight: number,
  padding = 1.1
): { cx: number; cz: number; span: number } {
  const cw = Math.max(canvasWidth, 1);
  const ch = Math.max(canvasHeight, 1);
  const bw = Math.max(bounds.maxX - bounds.minX, 1);
  const bh = Math.max(bounds.maxY - bounds.minY, 1);
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cz = (bounds.minY + bounds.maxY) / 2;
  const aspect = cw / ch;
  const halfH = (Math.max(bh, bw / aspect) / 2) * padding;
  const halfW = halfH * aspect;

  camera.left = -halfW;
  camera.right = halfW;
  camera.top = halfH;
  camera.bottom = -halfH;
  camera.zoom = 1;
  camera.position.set(cx, ORTHO_CAMERA_HEIGHT, cz);
  camera.up.set(0, 1, 0);
  camera.lookAt(cx, 0, cz);
  camera.near = 0.1;
  camera.far = ORTHO_CAMERA_HEIGHT * 4;
  camera.updateProjectionMatrix();

  return { cx, cz, span: Math.max(halfW, halfH) * 2 };
}
